import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { 
  Phone, 
  MapPin, 
  Clock, 
  DollarSign, 
  CheckCircle,
  Truck,
  ChefHat,
  Package,
  AlertCircle,
  X,
  Timer,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { DeliveryOrderWithDetails } from "@/hooks/useDeliveryOrders";

interface DeliveryOrderCardProps {
  order: DeliveryOrderWithDetails;
  onConfirm?: () => void;
  onMarkReady?: () => void;
  onSendForDelivery?: () => void;
  onConfirmDelivery?: () => void;
  onCancel?: () => void;
  isUpdating?: boolean;
}

const IFOOD_TIMEOUT_MS = 8 * 60 * 1000;

function IfoodCountdown({ createdAt }: { createdAt: string }) {
  const deadline = new Date(createdAt).getTime() + IFOOD_TIMEOUT_MS;
  const [remaining, setRemaining] = useState(() => deadline - Date.now());

  useEffect(() => {
    const id = setInterval(() => setRemaining(deadline - Date.now()), 1000);
    return () => clearInterval(id);
  }, [deadline]);

  if (remaining <= 0) {
    return (
      <div className="flex items-center justify-center gap-1 py-1 rounded-md bg-red-500/10 text-red-600 font-bold text-xs">
        <AlertCircle className="h-3 w-3" />
        EXPIRADO
      </div>
    );
  }

  const totalSeconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const label = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  const isCritical = remaining < 60 * 1000;
  const isWarning = remaining < 4 * 60 * 1000;

  return (
    <div
      className={cn(
        "flex items-center justify-center gap-1 py-1 rounded-md font-bold text-xs tabular-nums",
        isCritical
          ? "bg-red-500/10 text-red-600 animate-pulse"
          : isWarning
            ? "bg-amber-500/10 text-amber-600"
            : "bg-green-500/10 text-green-600",
      )}
    >
      <Timer className="h-3 w-3" />
      {label}
    </div>
  );
}

export function DeliveryOrderCard({
  order,
  onConfirm,
  onMarkReady,
  onSendForDelivery,
  onConfirmDelivery,
  onCancel,
  isUpdating,
}: DeliveryOrderCardProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const timeAgo = formatDistanceToNow(new Date(order.created_at), {
    locale: ptBR,
    addSuffix: true,
  });

  const totalPaid = order.payments?.reduce((sum, p) => sum + Number(p.amount), 0) || 0;
  const isPaid = totalPaid >= Number(order.total);
  const isIfood = order.external_source === "ifood";

  const getStatusColor = () => {
    switch (order.status) {
      case "pending":
        return "border-l-amber-500";
      case "preparing":
        return "border-l-blue-500";
      case "ready":
        return "border-l-green-500";
      case "out_for_delivery":
        return "border-l-purple-500";
      case "delivered":
        return "border-l-emerald-500";
      default:
        return "border-l-muted";
    }
  };

  return (
    <Card className={cn("border-l-4 transition-all", getStatusColor())}>
      <CardContent className="p-3 space-y-2">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-lg">#{order.order_number}</span>
              {isPaid && (
                <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Pago
                </Badge>
              )}
            </div>
            <p className="text-sm font-medium text-foreground truncate max-w-[180px]">
              {order.customer_name || "Cliente"}
            </p>
          </div>
          <div className="text-right">
            <p className="font-bold text-primary">
              {formatCurrency(Number(order.total))}
            </p>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {timeAgo}
            </p>
            {isIfood && (
              <Badge className="mt-1 bg-red-600 hover:bg-red-600 text-white border-0 text-[10px] px-1.5 py-0 h-4">
                iFood
              </Badge>
            )}
          </div>
        </div>

        {/* Contact & Address */}
        <div className="space-y-1 text-xs text-muted-foreground">
          {order.customer_phone && (
            <div className="flex items-center gap-1">
              <Phone className="h-3 w-3" />
              <span>{order.customer_phone}</span>
            </div>
          )}
          {order.delivery_address && (
            <div className="flex items-start gap-1">
              <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
              <span className="line-clamp-2">{order.delivery_address}</span>
            </div>
          )}
        </div>

        {/* Items summary */}
        <div className="text-xs text-muted-foreground border-t pt-2">
          {order.items?.slice(0, 3).map((item) => (
            <div key={item.id} className="flex justify-between">
              <span>{item.quantity}x {item.product_name}</span>
              <span>{formatCurrency(Number(item.total_price))}</span>
            </div>
          ))}
          {order.items && order.items.length > 3 && (
            <p className="text-muted-foreground/70 italic">
              +{order.items.length - 3} item(s)...
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="pt-2 border-t flex gap-2">
          {order.status === "pending" && isIfood && onConfirm && (
            <div className="flex-1 flex flex-col gap-2">
              <IfoodCountdown createdAt={order.created_at} />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={onConfirm}
                  disabled={isUpdating}
                  className="flex-1 gap-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="h-4 w-4" />
                  Aceitar
                </Button>
                <Button
                  size="sm"
                  onClick={onCancel}
                  disabled={isUpdating}
                  className="flex-1 gap-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  <X className="h-4 w-4" />
                  Recusar
                </Button>
              </div>
            </div>
          )}
          {order.status === "pending" && !isIfood && onConfirm && (
            <>
              <Button
                size="sm"
                onClick={onConfirm}
                disabled={isUpdating}
                className="flex-1 gap-1"
              >
                <CheckCircle className="h-4 w-4" />
                Confirmar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onCancel}
                disabled={isUpdating}
                className="text-destructive hover:text-destructive"
              >
                <AlertCircle className="h-4 w-4" />
              </Button>
            </>
          )}
          {order.status === "preparing" && onMarkReady && (
            <Button
              size="sm"
              onClick={onMarkReady}
              disabled={isUpdating}
              className="flex-1 gap-1"
            >
              <Package className="h-4 w-4" />
              Pronto
            </Button>
          )}
          {order.status === "ready" && onSendForDelivery && (
            <Button
              size="sm"
              onClick={onSendForDelivery}
              disabled={isUpdating}
              className="flex-1 gap-1"
            >
              <Truck className="h-4 w-4" />
              Enviar
            </Button>
          )}
          {order.status === "out_for_delivery" && onConfirmDelivery && (
            <Button
              size="sm"
              onClick={onConfirmDelivery}
              disabled={isUpdating}
              className="flex-1 gap-1 bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4" />
              Entregue
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
